import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_ROOMS, PUBLIC_ROOMS, MOCK_USERS } from '../mock';
import { RegisterModal, EditProfileModal, DeleteUserModal } from '../components/ProfileModals';
import { SiteRulesModal, TutorialModal } from '../components/ChatExtras';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Msg #986 + #987 — Forgot Password modal: emails the user a reset link via Resend.
// Username field (Msg #987) scopes the reset to a specific account when
// multiple usernames share an email (or when the caller isn't sure).
const ForgotPasswordModal = ({ open, onClose }) => {
  const [uname, setUname] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [err, setErr] = React.useState('');
  React.useEffect(() => {
    if (open) { setUname(''); setEmail(''); setMsg(''); setErr(''); setBusy(false); }
  }, [open]);
  if (!open) return null;
  const submit = async () => {
    setMsg(''); setErr('');
    if (!uname.trim()) {
      setErr('Enter your username.');
      return;
    }
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email.trim())) {
      setErr('Enter a valid email address.');
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/password_reset`, {
        email: email.trim(),
        username: uname.trim(),
      });
      setMsg(r.data?.message || 'If those details match an account, a reset link has been sent.');
    } catch (e) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      setErr(d || 'Could not send reset email.');
    } finally { setBusy(false); }
  };
  return (
    <div
      data-testid="forgot-password-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(20,40,20,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 460, maxWidth: '94vw',
          background: '#d6e8c5',
          border: '2px solid #2d6b1e',
          padding: '20px 26px',
          fontFamily: 'Arial, Helvetica, sans-serif',
          color: '#1c3f1c',
          boxShadow: '0 8px 26px rgba(0,0,0,0.35)',
        }}
      >
        <div style={{ fontWeight: 'bold', fontSize: 15, letterSpacing: 1, marginBottom: 4 }}>
          Forgot Password
        </div>
        <div style={{ fontSize: 12, marginBottom: 10, fontStyle: 'italic', opacity: 0.85 }}>
          Enter the username and email you registered with. A reset link will
          be sent to that email (valid for 30 minutes). For security we do not
          reveal whether the account exists in our records.
        </div>
        <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Username</label>
        <input
          data-testid="forgot-password-username"
          value={uname}
          onChange={(e) => setUname(e.target.value)}
          placeholder="your Mandala username"
          style={{ width: '100%', padding: '5px 8px', border: '1px solid #5b8c47',
                   fontFamily: 'inherit', fontSize: 13, marginBottom: 8 }}
          autoFocus
        />
        <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Email address</label>
        <input
          data-testid="forgot-password-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder="you@example.com"
          style={{ width: '100%', padding: '5px 8px', border: '1px solid #5b8c47',
                   fontFamily: 'inherit', fontSize: 13 }}
        />
        {err && (
          <div data-testid="forgot-password-error" style={{ color: '#7a1f1f', fontSize: 12, marginTop: 8 }}>
            {err}
          </div>
        )}
        {msg && (
          <div data-testid="forgot-password-msg" style={{ color: '#1c5a1c', fontSize: 12, marginTop: 8 }}>
            {msg}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button
            onClick={onClose}
            data-testid="forgot-password-cancel"
            style={{ background: '#5a4a1d', color: '#f5e6c0',
                     border: '1px solid #3a2b14', padding: '4px 14px',
                     fontWeight: 'bold', cursor: 'pointer' }}
          >Close</button>
          <button
            data-testid="forgot-password-submit"
            onClick={submit}
            disabled={busy}
            style={{ background: '#1c3a1c', color: '#ecfcd9',
                     border: '1px solid #2d6b1e', padding: '4px 14px',
                     fontWeight: 'bold', cursor: busy ? 'wait' : 'pointer',
                     opacity: busy ? 0.7 : 1 }}
          >{busy ? 'Sending…' : 'Send Reset Link'}</button>
        </div>
      </div>
    </div>
  );
};

const LoginPage = () => {
  const navigate = useNavigate();
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [defaultRoom, setDefaultRoom] = React.useState('');
  const [publicRoom, setPublicRoom] = React.useState('');
  const [error, setError] = React.useState('');
  const [showRegister, setShowRegister] = React.useState(false);
  const [showEdit, setShowEdit] = React.useState(false);
  const [showDelete, setShowDelete] = React.useState(false);
  const [showRules, setShowRules] = React.useState(false);
  const [showTutorial, setShowTutorial] = React.useState(false);
  const [showAgeCheck, setShowAgeCheck] = React.useState(false);
  const [pendingLogin, setPendingLogin] = React.useState(null);
  const [showForgot, setShowForgot] = React.useState(false);

  const activeUsers = 0;

  const handleChat = async () => {
    if (!username.trim()) {
      setError('Please enter a username.');
      return;
    }
    // Server-side ban check
    try {
      const r = await axios.get(`${API}/admin/is_banned`, { params: { name: username.trim() } });
      if (r.data && r.data.banned) {
        setError('This username is banned from Mandala.');
        return;
      }
    } catch (e) { void e; }
    // Msg #802 — Verify against the admin-approved user store. Reccult
    // (master admin) passes with the master password `kittymew`. Other users
    // must have been approved via /api/auth/admin_approve.
    try {
      const auth = await axios.post(`${API}/auth/login`, {
        username: username.trim(),
        password: password,
      });
      if (!auth.data || !auth.data.ok) {
        setError('Login failed. Please check your credentials.');
        return;
      }
      // Msg #988 — Persist the user's saved font_color for instant paint on chat.
      if (auth.data.font_color) {
        localStorage.setItem('mandala_font_color', auth.data.font_color);
      }
      // Msg #989 — Persist backend canonical user_id so HP + NPC affection
      // survive logout/tab-close (previously session-scoped id → reset).
      if (auth.data.user_id) {
        sessionStorage.setItem('mandala_login_user_id', auth.data.user_id);
        const uname = (auth.data.username || username || '').toLowerCase();
        localStorage.setItem('mandala_user_id::' + uname, auth.data.user_id);
      }
    } catch (e) {
      const detail = e && e.response && e.response.data && e.response.data.detail;
      const status = e && e.response && e.response.status;
      if (status === 404) {
        setError(detail || 'No account with that name — click Register to create one.');
      } else if (status === 401) {
        setError(detail || 'Incorrect password. If you set a first-login password before, use that one.');
      } else {
        setError(detail || 'Login failed. Please try again.');
      }
      return;
    }
    const room = publicRoom || defaultRoom || 'MainHall';
    // Msg #796 — Show the 18+ disclaimer on EVERY login again (was suppressed
    // permanently via localStorage). We now use sessionStorage so it appears
    // once per browsing session instead — brought back per user request.
    if (sessionStorage.getItem('mandala_age_confirmed_session')) {
      sessionStorage.setItem('keep_username', username.trim());
      sessionStorage.setItem('keep_room', room);
      navigate('/chat');
      return;
    }
    setPendingLogin({ name: username.trim(), room });
    setShowAgeCheck(true);
  };

  const confirmAge = () => {
    if (!pendingLogin) return;
    sessionStorage.setItem('keep_username', pendingLogin.name);
    sessionStorage.setItem('keep_room', pendingLogin.room);
    sessionStorage.setItem('mandala_age_confirmed_session', new Date().toISOString());
    // Keep the persistent flag too so automation tests can still bypass.
    localStorage.setItem('mandala_age_confirmed', new Date().toISOString());
    setShowAgeCheck(false);
    navigate('/chat');
  };

  const declineAge = () => {
    setShowAgeCheck(false);
    setPendingLogin(null);
    setError('You must confirm you are of legal age to enter Mandala.');
  };

  return (
    <div className="keep-bg min-h-screen">
      <div className="keep-header-bar w-full" />

      <div className="max-w-[900px] mx-auto px-4 pt-6 pb-2">
        <div className="keep-frame">
          <div className="p-8">
            <div className="text-center text-[12px]">
              Contact the Admin at{' '}
              <a href="mailto:intenseresolve@aol.com" className="keep-link">
                intenseresolve@aol.com
              </a>
            </div>

            <h1 className="keep-title mt-5">Mandala</h1>

            <div className="text-center mt-4">
              <span className="keep-subtitle-link mx-2" style={{ cursor: 'pointer' }} onClick={() => setShowTutorial(true)}>Tutorial</span>
              <span className="keep-subtitle-link mx-2" style={{ cursor: 'pointer' }} onClick={() => setShowRules(true)}>Site Rules</span>
              <span className="keep-subtitle-link mx-2">Forums</span>
            </div>

            <div className="text-center mt-5 text-[13px]">
              Messages are deleted after .3 hours and usernames after 5 minutes ...
            </div>

            <div className="text-center mt-3 text-[13px]">
              There are currently{' '}
              <span className="keep-link font-bold">{activeUsers} users</span>{' '}
              in the chat.
            </div>

            {/* LOGIN BOX */}
            <div className="flex justify-center mt-5">
              <div className="keep-login-box w-[560px]">
                <div className="text-center font-bold mb-3">Please enter ...</div>
                <table className="mx-auto" cellPadding="4">
                  <tbody>
                    <tr>
                      <td className="text-right">Your Username :</td>
                      <td>
                        <input
                          data-testid="login-username"
                          className="keep-input"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleChat()}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="text-right">Your Password :</td>
                      <td>
                        <input
                          data-testid="login-password"
                          type="password"
                          className="keep-input"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleChat()}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div className="text-center font-bold mt-4">Account Management</div>
                <div className="text-center mt-2 text-[13px]">
                  <span className="keep-link" onClick={() => setShowRegister(true)}>Register</span>
                  {' | '}
                  <span className="keep-link" onClick={() => setShowEdit(true)}>Edit your Profile</span>
                  {' | '}
                  <span className="keep-link" onClick={() => setShowDelete(true)}>Delete User</span>
                </div>
                {/* Msg #986 — Password reset entry point. */}
                <div className="text-center mt-1 text-[12px]">
                  <span
                    className="keep-link"
                    data-testid="login-forgot-password"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setShowForgot(true)}
                  >Forgot password?</span>
                </div>

                <div className="text-center font-bold mt-4">Select a chat room ...</div>
                <table className="mx-auto mt-2" cellPadding="4">
                  <tbody>
                    <tr>
                      <td className="text-right">Default Rooms :</td>
                      <td>
                        <select
                          data-testid="login-default-room"
                          className="keep-select"
                          value={defaultRoom}
                          onChange={(e) => {
                            setDefaultRoom(e.target.value);
                            setPublicRoom('');
                          }}
                        >
                          <option value="">Make your choice ...</option>
                          {DEFAULT_ROOMS.map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                    {PUBLIC_ROOMS.length >= 0 && (
                      <tr>
                        <td className="text-right">Public rooms created by users :</td>
                        <td>
                          <select
                            className="keep-select"
                            value={publicRoom}
                            onChange={(e) => {
                              setPublicRoom(e.target.value);
                              setDefaultRoom('');
                            }}
                          >
                            <option value="">Make your choice ...</option>
                            {PUBLIC_ROOMS.map((r) => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <div className="text-center text-[12px] mt-4 leading-5">
                  <b>By entering the chat,</b>
                  <br />
                  <b>you agree that you have read</b>
                  <br />
                  <b>and will abide by the <span className="keep-link" onClick={() => setShowRules(true)}>rules</span> of the site</b>
                </div>

                {error && (
                  <div className="text-center text-[12px] text-red-700 mt-3">{error}</div>
                )}

                <div className="text-center mt-4">
                  <button data-testid="login-chat-now" className="keep-btn" onClick={handleChat}>
                    CHAT NOW
                  </button>
                  <span className="ml-2">...</span>
                </div>
              </div>
            </div>

            {/* Footer text */}
            <div className="keep-footer-text mt-6">
              &copy; 2001 - 2006 <b>Minion Development Corp.</b>
              <br />
              Based on <span className="keep-link">PHPMyChat</span>
            </div>
          </div>
        </div>
      </div>

      <div className="keep-disclaimer">
        Disclaimer: This Site may contain scenarios that are not recommended for people who may become upset at satire, parody or opinions that are not their own in regards to religion, art, entertainment, gaming, history, politics, sex, or the nature of the universe.
        <br />
        &copy; Mandala - All rights reserved
      </div>

      <RegisterModal open={showRegister} onClose={() => setShowRegister(false)} />
      <EditProfileModal open={showEdit} onClose={() => setShowEdit(false)} />
      <DeleteUserModal open={showDelete} onClose={() => setShowDelete(false)} />
      <SiteRulesModal open={showRules} onClose={() => setShowRules(false)} />
      <TutorialModal open={showTutorial} onClose={() => setShowTutorial(false)} />
      <ForgotPasswordModal open={showForgot} onClose={() => setShowForgot(false)} />

      {showAgeCheck && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(20, 40, 20, 0.65)',
            zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: 520, maxWidth: '94vw',
              backgroundColor: '#d6e8c5',
              border: '2px solid #2d6b1e',
              boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
              padding: '22px 28px',
              fontFamily: 'Arial, Helvetica, sans-serif',
              color: '#1c3f1c',
              textAlign: 'center',
            }}
          >
            <div style={{ fontWeight: 'bold', fontSize: 20, marginBottom: 8 }}>
              Adult Content Disclosure
            </div>
            <div
              style={{
                textAlign: 'left', fontSize: 13, lineHeight: 1.55,
                backgroundColor: '#eaf3da', border: '1px solid #5b8c47',
                padding: '10px 12px', marginTop: 8,
              }}
            >
              Mandala is an adults-only roleplay community. Some rooms (including but not
              limited to <b>Night_Club</b>, <b>Strip_Club</b> and <b>Inn_Brothel</b>) contain
              mature themes, suggestive language, and sensual roleplay between consenting
              adult characters.
              <br /><br />
              By entering, you confirm that:
              <ul style={{ margin: '6px 0 6px 22px', padding: 0 }}>
                <li>You are <b>of legal adult age</b> in your jurisdiction.</li>
                <li>You have read and accept the{' '}
                  <span
                    className="keep-link"
                    style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={() => setShowRules(true)}
                  >
                    Mandala Site Rules / AUP
                  </span>.
                </li>
                <li>You understand that interactions with other users and AI-driven NPCs
                  may include mature themes, and you enter voluntarily and at your own risk.</li>
              </ul>
            </div>

            <div style={{ marginTop: 14, fontWeight: 'bold' }}>
              Are you of legal adult age and do you agree to enter?
            </div>

            <div style={{ marginTop: 16 }}>
              <button
                data-testid="age-gate-yes"
                onClick={confirmAge}
                style={{
                  backgroundColor: '#1c3a1c', color: '#ecfcd9',
                  border: '1px solid #2d6b1e',
                  padding: '6px 22px', fontWeight: 'bold', fontSize: 13,
                  cursor: 'pointer', margin: '0 6px', minWidth: 80,
                }}
              >
                Yes
              </button>
              <button
                data-testid="age-gate-no"
                onClick={declineAge}
                style={{
                  backgroundColor: '#5a2828', color: '#fce2e2',
                  border: '1px solid #3a1a1a',
                  padding: '6px 22px', fontWeight: 'bold', fontSize: 13,
                  cursor: 'pointer', margin: '0 6px', minWidth: 80,
                }}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
